// India PIN code → city / state / area lookup
// Uses the free public postalpincode.in API. No key or CORS proxy needed.
// Docs: http://postalpincode.in/Api-Details

import { useEffect, useRef, useState } from "react";
import { devWarn } from "./devLog";

const CACHE = new Map(); // in-memory cache — same PIN won't hit the API twice
const IN_FLIGHT = new Map(); // dedupe concurrent lookups for the same PIN

async function fetchPincode(pin) {
  if (CACHE.has(pin)) return CACHE.get(pin);
  if (IN_FLIGHT.has(pin)) return IN_FLIGHT.get(pin);
  const p = (async () => {
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (!Array.isArray(data) || data[0]?.Status !== "Success" || !data[0]?.PostOffice?.length) {
        return null;
      }
      const offices = data[0].PostOffice;
      // Prefer HO (Head Office), then SO (Sub Office), then anything
      const preferred = offices.find(o => o.BranchType === "Head Post Office")
                     || offices.find(o => o.BranchType === "Sub Post Office")
                     || offices[0];
      const out = {
        city: preferred.District || preferred.Division || "",
        state: preferred.State || "",
        area: preferred.Name || "",
        country: "India",
        // Full list so admin/callers can also present a locality picker if they want
        localities: offices.map(o => o.Name).filter(Boolean),
      };
      CACHE.set(pin, out);
      return out;
    } catch (err) {
      devWarn("Pincode lookup failed:", err);
      return null;
    } finally {
      IN_FLIGHT.delete(pin);
    }
  })();
  IN_FLIGHT.set(pin, p);
  return p;
}

/**
 * Watch `pin` (a 6-digit Indian PIN code string). When it becomes 6 digits,
 * hit the postalpincode.in API and call `onResolved({ city, state, area, country, localities })`.
 * The caller decides whether to overwrite user-entered fields.
 */
export function usePincodeLookup(pin, onResolved) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastPinRef = useRef("");

  useEffect(() => {
    const p = (pin || "").replace(/\D/g, "");
    if (p.length !== 6 || !/^[1-9]\d{5}$/.test(p)) {
      lastPinRef.current = "";
      setError(null);
      return;
    }
    if (p === lastPinRef.current) return;
    lastPinRef.current = p;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPincode(p)
      .then((result) => {
        if (cancelled) return;
        if (!result) { setError("Couldn't find that PIN — please fill city/state manually"); return; }
        onResolved(result);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return { loading, error };
}
